import React from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import ChassisWithExternalDropdown from '../../../../../../components/common/ChassisWithExternalDropdown';
import CustomerDropdown from '../../../../../../components/common/CustomerDropdown';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import ChassisInfoDialog from '../chassis-info/ChassisInfoDialog';
import WarrantyStatusBanner from '../components/WarrantyStatusBanner';

const FormRow = ({ label, icon, error, children, className = "col-12 md:col-6" }) => (
    <div className={className}>
        <div className="field">
            <label className="text-600" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {icon && <i className={`${icon} mr-2`} style={{ color: '#94a3b8' }} />}
                {label}
            </label>
            {children}
            {error && <small className="p-error block mt-1">{error}</small>}
        </div>
    </div>
);

const SectionDivider = ({ icon, title }) => (
    <div className="col-12">
        <div className="flex align-items-center mt-4 mb-1">
            <i className={`${icon} mr-2`} style={{ fontSize: '0.9rem', color: '#64748b' }} />
            <span className="font-medium" style={{ fontSize: '0.85rem', color: '#64748b', letterSpacing: '0.03em', textTransform: 'uppercase' }}>{title}</span>
            <div className="flex-1 ml-3" style={{ height: '1px', background: '#e2e8f0' }} />
        </div>
    </div>
);

const InfoCardItem = ({ icon, label, value }) => (
    <div className="col-12 md:col-3">
        <div className="mb-2 md:mb-0">
            <span className="font-semibold text-600 block text-sm mb-1">
                <i className={`${icon} mr-1`} /> {label}
            </span>
            <span className="text-900 font-medium">{value}</span>
        </div>
    </div>
);

const DeclarationFormFields = ({
    formData,
    setFormData,
    errors,
    handleChassisChange,
    hasWarrantyClaim,
    editMode,
    onCreateNewCustomer,
    chassisData,
    customerOptions,
    onKmChange,
    onDateChange,
    openBulletinsCount = 0,
    onOpenBulletinsDialog,
    isFetchingOpenBulletins = false
}) => {
    const { t } = useLanguage();
    const [chassisInfoVisible, setChassisInfoVisible] = React.useState(false);

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    return (
        <div className="grid">
            <div className="col-12">
                <WarrantyStatusBanner
                    startDate={formData.warrantyBeginDate}
                    endDate={formData.warrantyEndDate}
                    checkDate={formData.serviceDeliveryDate}
                />
            </div>

            {/* Chassis Selection */}
            <FormRow
                className="col-12"
                label={t('warranty.customerDeclaration.dialog.fields.chassisNumber')}
                icon="pi pi-hashtag"
                error={errors.chassisNumber}
            >
                <div className="flex gap-2">
                    <div className="flex-1">
                        <ChassisWithExternalDropdown
                            value={formData.chassisNumber}
                            onChange={handleChassisChange}
                            className={errors.chassisNumber ? 'p-invalid' : ''}
                            disabled={hasWarrantyClaim}
                            showDescription={false}
                        />
                    </div>
                    <Button
                        icon="pi pi-history"
                        className="p-button-outlined"
                        style={{ minWidth: '2.5rem' }}
                        onClick={() => setChassisInfoVisible(true)}
                        tooltip={t('common.details')}
                    />
                    {!editMode && !!formData.chassisNumber && onOpenBulletinsDialog && (
                        <Button
                            type="button"
                            icon="pi pi-clipboard"
                            label={t('warranty.customerDeclaration.openBulletins.button')}
                            severity={openBulletinsCount > 0 ? 'warning' : 'secondary'}
                            outlined
                            loading={isFetchingOpenBulletins}
                            onClick={onOpenBulletinsDialog}
                            tooltip={t('warranty.customerDeclaration.openBulletins.tooltip')}
                            style={{ whiteSpace: 'nowrap', width: 'auto', flex: '0 0 auto' }}
                        >
                            <Tag
                                value={openBulletinsCount}
                                severity={openBulletinsCount > 0 ? 'warning' : 'secondary'}
                                className="ml-2"
                            />
                        </Button>
                    )}
                </div>
            </FormRow>

            {/* Chassis Info Display Panel */}
            {(!editMode || formData.chassisNumber) && (
                <div className="col-12">
                    <div className="surface-50 p-3 border-round border-1 border-200 mb-2">
                        <div className="grid">
                            <InfoCardItem
                                icon="pi pi-info-circle"
                                label={t('warranty.customerDeclaration.dialog.fields.chassisDescription')}
                                value={formData.chassisDescription || chassisData?.translatedText || '-'}
                            />
                            <InfoCardItem
                                icon="pi pi-stopwatch"
                                label={t('warranty.customerDeclaration.dialog.fields.lastKmInfo')}
                                value={chassisData?.lastKmInfo?.toLocaleString('tr-TR') || '-'}
                            />
                            <InfoCardItem
                                icon="pi pi-calendar-plus"
                                label={t('warranty.customerDeclaration.dialog.fields.warrantyBeginDate')}
                                value={formatDate(formData.warrantyBeginDate || chassisData?.warrantyBeginDate)}
                            />
                            <InfoCardItem
                                icon="pi pi-calendar-minus"
                                label={t('warranty.customerDeclaration.dialog.fields.warrantyEndDate')}
                                value={formatDate(formData.warrantyEndDate || chassisData?.warrantyEndDate)}
                            />
                        </div>
                        {!editMode && !String(formData.chassisNumber || '').trim() && (
                            <small className="p-error block mt-2">{t('warranty.customerDeclaration.messages.chassisDetailRequirements')}</small>
                        )}
                    </div>
                </div>
            )}


            {/* ── KM & Diğer ── */}
            {/* <SectionDivider icon="pi pi-compass" title={t('warranty.customerDeclaration.dialog.sections.kmAndOtherInfo')} /> */}

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.currentKm')} icon="pi pi-compass" error={errors.kmInformation}>
                <InputNumber
                    value={formData.kmInformation}
                    onValueChange={(e) => {
                        updateField('kmInformation', e.value);
                        onKmChange?.(e.value);
                    }}
                    className={errors.kmInformation ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.kilometer')}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.serviceDeliveryDate')} icon="pi pi-calendar-plus" error={errors.serviceDeliveryDate}>
                <Calendar
                    value={formData.serviceDeliveryDate ? new Date(formData.serviceDeliveryDate) : null}
                    onChange={(e) => {
                        const iso = e.value?.toISOString() || null;
                        updateField('serviceDeliveryDate', iso);
                        onDateChange?.(iso);
                    }}
                    className={errors.serviceDeliveryDate ? 'p-invalid' : ''}
                    showIcon
                    disabled={false}
                />
            </FormRow>


            
            {/* Customer Information */}
            <SectionDivider icon="pi pi-user" title={t('warranty.customerDeclaration.dialog.sections.customerInfo')} />

            <FormRow
                label={t('warranty.customerDeclaration.dialog.fields.customerId')}
                icon="pi pi-user"
                error={errors.customer}
            >
                <div className="flex gap-2">
                    <div className="flex-1">
                        <CustomerDropdown
                            value={formData.customer}
                            onChange={(id, data) => {
                                updateField('customer', id);

                                if (data) {
                                    updateField('customerPhone', data.phoneNumber || data.phone || '');
                                    updateField('plateNumber', data.licensePlate || data.plate || '');
                                    updateField('customerName', data.name);
                                    updateField('customerNameSurname', data.name);
                                    updateField('balanceInfo', data.bakiyeBilgisi || '');
                                } else {
                                    updateField('customerPhone', '');
                                    updateField('plateNumber', '');
                                    updateField('customerName', '');
                                    updateField('customerNameSurname', '');
                                    updateField('balanceInfo', '');
                                }
                            }}
                            className={errors.customer ? 'p-invalid' : 'w-full'}
                            disabled={hasWarrantyClaim}
                            chassisNo={formData.chassisNumber}
                            customerOptions={customerOptions}
                            suppressCustomerFetch={!!customerOptions?.length}
                        />
                    </div>
                    <Button
                        icon="pi pi-plus"
                        className="p-button-outlined"
                        onClick={onCreateNewCustomer}
                        tooltip={t('customerDefinition.newTitle')}
                        type="button"
                        disabled={hasWarrantyClaim}
                        style={{ minWidth: '2.5rem' }}
                    />
                </div>
            </FormRow>

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.customerPhone')} icon="pi pi-phone" error={errors.customerPhone}>
                <InputText
                    value={formData.customerPhone}
                    onChange={(e) => updateField('customerPhone', e.target.value)}
                    className={errors.customerPhone ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.customerPhone')}
                    disabled={true}
                />
            </FormRow>


            <FormRow label={t('warranty.customerDeclaration.dialog.fields.balanceInfo')} icon="pi pi-wallet">
                <InputText
                    value={formData.balanceInfo || '0'}
                    disabled={true}
                />
            </FormRow>

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.plateNumber')} icon="pi pi-credit-card">
                <InputText
                    value={formData.plateNumber}
                    onChange={(e) => updateField('plateNumber', e.target.value)}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.plateNumber')}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>

            {/* ── Beyan & Notlar ── */}
            <SectionDivider icon="pi pi-file-text" title={t('warranty.customerDeclaration.dialog.sections.description')} />

            <FormRow className="col-12" label={t('warranty.customerDeclaration.dialog.fields.declaration')} icon="pi pi-pencil" error={errors.customerStatement}>
                <InputTextarea
                    value={formData.customerStatement || ''}
                    onChange={(e) => updateField('customerStatement', e.target.value)}
                    rows={3}
                    className={errors.customerStatement ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.declaration')}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>

            <FormRow className="col-12" label={t('warranty.customerDeclaration.dialog.fields.serviceNote')} icon="pi pi-file-edit">
                <InputTextarea
                    value={formData.serviceNote || ''}
                    onChange={(e) => updateField('serviceNote', e.target.value)}
                    rows={3}
                    disabled={hasWarrantyClaim}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.serviceNote')}
                />
            </FormRow>

            {/* ── Tarih Bilgileri ── */}
            <SectionDivider icon="pi pi-calendar" title={t('warranty.customerDeclaration.dialog.sections.dateInfo')} />

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.registrationDate')} icon="pi pi-calendar" error={errors.registrationDate}>
                <Calendar
                    value={formData.registrationDate ? new Date(formData.registrationDate) : null}
                    onChange={(e) => updateField('registrationDate', e.value?.toISOString() || null)}
                    className={errors.registrationDate ? 'p-invalid' : ''}
                    showIcon
                    placeholder={!editMode ? t('warranty.customerDeclaration.dialog.placeholders.registrationDatePlaceHolder') : ''}
                    disabled={true}
                />
            </FormRow>

            {/* ── Teslim Bilgileri ── */}
            <SectionDivider icon="pi pi-users" title={t('warranty.customerDeclaration.dialog.sections.deliveryInfo')} />

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.receivedBy')} icon="pi pi-download" error={errors.receivingPerson}>
                <InputText
                    value={formData.receivingPerson}
                    onChange={(e) => updateField('receivingPerson', e.target.value)}
                    className={errors.receivingPerson ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.receivedBy')}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.deliveredBy')} icon="pi pi-upload" error={errors.receivingDeliveryPerson}>
                <InputText
                    value={formData.receivingDeliveryPerson}
                    onChange={(e) => updateField('receivingDeliveryPerson', e.target.value)}
                    className={errors.receivingDeliveryPerson ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.deliveredBy')}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>

            <FormRow label={t('warranty.customerDeclaration.dialog.fields.deliveryPhone')} icon="pi pi-phone" error={errors.deliveryPhone}>
                <InputText
                    value={formData.deliveryPhone || ''}
                    onChange={(e) => updateField('deliveryPhone', e.target.value)}
                    className={errors.deliveryPhone ? 'p-invalid' : ''}
                    placeholder={t('warranty.customerDeclaration.dialog.placeholders.deliveryPhone')}
                    maxLength={30}
                    disabled={hasWarrantyClaim}
                />
            </FormRow>


            <ChassisInfoDialog
                visible={chassisInfoVisible}
                onHide={() => setChassisInfoVisible(false)}
                chassisNo={formData.chassisNumber}
            />
        </div>
    );
};

export default React.memo(DeclarationFormFields);
